import type { LockedReason } from '@web-app/components/Calendar/CalendarView.helpers';
import type {
  CalendarAppointment,
  UpcomingAppointment,
} from '@web-app/server/services/appointments/types';
import type {
  OneTimeKey,
  WindowKey,
} from '@web-app/server/services/availability';
import { atom } from 'jotai';

/**
 * Global scheduling drawer state.
 *
 * The drawer is a generic multi-page host (mounted once in _app.tsx) that
 * renders one page per *kind* (appointment / availability / block / synced).
 * Instead of a flat bag of per-entity atoms that can drift out of sync, the
 * drawer is a small **route stack**: the current page is the last route, and an
 * empty stack means closed. A stack (not a single route) because some flows are
 * multi-step within a kind (book → patient-search → slot-picker; reschedule
 * pushes a slot-picker on top of a detail page) and "back" then works for free.
 *
 * Plain global atoms (no Bunshi molecule) — the drawer is a singleton, same
 * pattern as the chat drawer.
 */

export type DrawerKind = 'appointment' | 'availability' | 'block' | 'synced';

/** detail = read-only; create = new entity; edit = mutate existing. */
export type DrawerMode = 'detail' | 'create' | 'edit';

/** Subject for the appointment kind (book / reschedule a follow-up). */
export interface AppointmentSubject {
  patientId: string;
  patientName: string;
  /** 'book' for a new follow-up; 'reschedule' when replacing an existing one. */
  bookingMode: 'book' | 'reschedule';
  /** The appointment being rescheduled (absent for a fresh booking). */
  existingAppointment?: UpcomingAppointment | null;
  /**
   * True for the calendar "Add to calendar" entry, where the provider picks the
   * patient inline on the single booking page. Absent/false when the patient is
   * fixed by the entry point (in-session / reschedule) so the picker is hidden.
   */
  allowPatientSelect?: boolean;
}

/**
 * Subject for availability-edit. Includes the clicked calendar day when opened
 * from the grid (for scoped this / this-and-future / all).
 */
export interface AvailabilityEditSubject {
  window: WindowKey;
  /** YYYY-MM-DD of the clicked occurrence. */
  occurrenceDate?: string;
}

/** Subject for editing a one-day window. It has no series, hence no scope. */
export interface OneTimeAvailabilityEditSubject {
  oneTimeWindow: OneTimeKey;
}

/** Subject for a block no key can reach — explain why, offer Remove. */
export interface LockedAvailabilitySubject {
  recordIds: string[];
  reason: LockedReason;
  label: string;
  hours: string;
  sourceTimezone?: string;
  projectionReason?: 'dst-mismatch' | 'crosses-midnight';
}

/**
 * The per-kind subject carried by a route. A union that widens as each kind's
 * PR registers its descriptor. Consumers narrow by `route.kind` + `route.mode`:
 * appointment-create carries an AppointmentSubject (booking), appointment-detail
 * carries the CalendarAppointment being viewed, availability-edit carries an
 * AvailabilityEditSubject.
 */
export type DrawerSubject =
  | AppointmentSubject
  | CalendarAppointment
  | AvailabilityEditSubject
  | OneTimeAvailabilityEditSubject
  | LockedAvailabilitySubject;

export interface DrawerRoute {
  kind: DrawerKind;
  mode: DrawerMode;
  subject?: DrawerSubject;
}

// --- Input: the single source of truth ---

/** The route stack. Current page = last entry; `[]` = drawer closed. */
export const drawerStackAtom = atom<DrawerRoute[]>([]);

/**
 * Which side the drawer slides in from — left (in-session) or right
 * (profile / calendar). Orthogonal presentation, set once at open time.
 */
export const drawerSideAtom = atom<'left' | 'right'>('right');

// --- Derived: read-only, never duplicated state ---

export const drawerOpenAtom = atom((get) => get(drawerStackAtom).length > 0);

export const currentRouteAtom = atom<DrawerRoute | null>(
  (get) => get(drawerStackAtom).at(-1) ?? null,
);

// --- Actions: write-only, encapsulate every transition ---

/** Open the drawer at a fresh root route (replaces any existing stack). */
export const openRouteAtom = atom(null, (_get, set, route: DrawerRoute) => {
  set(drawerStackAtom, [route]);
});

/** Push a page on top of the current one (multi-step within a kind). */
export const pushRouteAtom = atom(null, (get, set, route: DrawerRoute) => {
  set(drawerStackAtom, [...get(drawerStackAtom), route]);
});

/** Replace the current page in place (no new history entry). */
export const replaceRouteAtom = atom(null, (get, set, route: DrawerRoute) => {
  set(drawerStackAtom, [...get(drawerStackAtom).slice(0, -1), route]);
});

/** Pop back to the previous page (or close if it was the last). */
export const popRouteAtom = atom(null, (get, set) => {
  set(drawerStackAtom, get(drawerStackAtom).slice(0, -1));
});

/** Close the drawer (clear the stack). */
export const closeDrawerAtom = atom(null, (_get, set) => {
  set(drawerStackAtom, []);
});
